import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../api/apiService';
import { ToastSuccess, ToastError } from "../components/toast-notification";

function ResetPassword() {
  const params = useParams();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState({ password: '', newPassword: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'password') {
      if (!value) error = 'Password is required';
      else if (value.length < 6) error = 'Password must be at least 6 characters';
    }
    if (name === 'newPassword') {
      if (!value) error = 'Confirm Password is required';
      else if (value !== formValues.password) error = 'Passwords do not match';
    }
    if (error) setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    let errors = {};
    if (!formValues.password) errors.password = 'Password is required';
    else if (formValues.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (!formValues.newPassword) errors.newPassword = 'Confirm Password is required';
    else if (formValues.newPassword !== formValues.password) errors.newPassword = 'Passwords do not match';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tryResetPassword = (bodyData) => {
    const reqBody = { ...bodyData, token: params.token };
    resetPassword(reqBody)
      .then(function (response) { ToastSuccess(response?.data?.message); setIsLoading(false); navigate("/login"); })
      .catch(function (error) { ToastError(error?.response?.data?.message); setIsLoading(false); });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) { setIsLoading(true); tryResetPassword(formValues); }
  };

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#030305]">

      {/* LEFT PANEL: Cyberpunk Visuals */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 border-r border-fuchsia-900/30">
        <div className="absolute inset-0 cyber-auth-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#030305]" />

        <div className="relative z-10 flex items-center gap-4 mb-auto">
          <div className="w-10 h-10 border border-fuchsia-500 bg-fuchsia-900/30 flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
            <svg className="w-5 h-5 text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <span className="text-2xl font-black text-fuchsia-400 tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,0,255,0.4)]">
            EXTENDSWARE
          </span>
        </div>

        <div className="relative z-10 my-auto">
          <h2 className="text-[4rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-fuchsia-500 to-cyan-400 drop-shadow-[0_0_15px_rgba(255,0,255,0.3)]">
            SYSTEM
            <br />
            RE-KEY
          </h2>
          <div className="mt-6 flex flex-col gap-2 font-mono text-fuchsia-500/60 text-xs">
            <p> UPLINK VERIFIED...</p>
            <p> OPERATOR IDENTITY CONFIRMED...</p>
            <p> AWAITING NEW SECURITY DESIGNATION...</p>
            <div className="h-4 w-32 bg-fuchsia-500/20 border border-fuchsia-500/30 mt-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-4/5 bg-fuchsia-500/50 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-auto flex items-center justify-between text-[10px] font-mono text-fuchsia-900">
          <span>SYS.VER: 9.4.2</span>
          <span>ENCRYPTION: 256-BIT NEURAL</span>
        </div>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-fuchsia-900/10 via-[#030305] to-[#030305] lg:hidden" />

        <div className="w-full max-w-md cyber-card p-8 md:p-10 relative z-10 shadow-none border-none lg:bg-transparent lg:backdrop-filter-none">

          {/* Header */}
          <div className="mb-10">
            <h1 className="text-2xl font-black text-white tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] mb-2">
              SYS.SET_NEW_KEY
            </h1>
            <p className="cyber-label text-[10px]">
              ESTABLISH NEW SECURITY KEY TO RESTORE MAINFRAME ACCESS.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* New Password */}
            <div>
              <label htmlFor="password" className="cyber-label block mb-2">NEW_SECURITY_KEY</label>
              <input id="password" name="password" type="password"
                className={`cyber-input w-full p-3 font-mono ${formErrors.password ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="********" value={formValues.password} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.password && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.password}
              </p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="newPassword" className="cyber-label block mb-2">CONFIRM_SECURITY_KEY</label>
              <input id="newPassword" name="newPassword" type="password"
                className={`cyber-input w-full p-3 font-mono ${formErrors.newPassword ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="********" value={formValues.newPassword} onChange={handleChange} onBlur={handleBlur} />
                {formErrors.newPassword && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                  [!] {formErrors.newPassword}
              </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              className="cyber-btn cyber-btn-magenta w-full py-4 mt-2">
              {isLoading ? "UPDATING_KEY..." : "APPLY_NEW_KEY"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
